import { Box, Typography } from "@mui/material";
import CustomToolTip from "../../../components/CustomTooltip";
import StyledDataGrid from "../../../components/StyledDataGrid";
import SvgToIcon from "../../../icons/SvgToIcon";

import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import { platformUsersKeys } from "../../../api/queryKeys";
import LoadingIcon from "../../../components/LoadingIcon";
import { Toast } from "../../../components/Toast";
import {
  MSSP_PLATFORM_ACCESS_ROLES,
  UserPlatformAccessRole,
} from "../../../utils/constants";
import { getPrettyDateTime, useLocalTime } from "../../../utils/timestamp";
import { IntegrationsMapping } from "../../stack/integrations/IntegrationsMapping";
import ChangeAccessRoleDialog from "./ChangeAccessRoleDialog";
import DeleteOrEditMember from "./DeleteOrEditMember";
import FieldMenu from "./FieldMenu";
import { getDisplayEmail } from "./utils";

export default function PlatformUsersTable({
  platformUsersList,
  currentPlatformUser,
  refToPass,
  titleOptions,
}) {
  const { updatePlatformUserAccessRole, updatePlatformUserTitle } = useAPI();
  const queryClient = useQueryClient();
  const { trackEvent } = useAPI();
  const showLocalTime = useLocalTime();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRoleChange, setSelectedRoleChange] = useState(null);

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedRoleChange(null);
  };

  const handleAccessRoleChange = (email, role) => {
    setSelectedRoleChange({ email, role });
    setIsDialogOpen(true);
  };

  const handleConfirmRoleChange = async () => {
    handleDialogClose();

    if (!selectedRoleChange) {
      return;
    }
    const { email, role } = selectedRoleChange;

    await updatePlatformUserAccessRole(email, role)
      .then(() => {
        trackEvent(`User changed access role for user ${email} to ${role}`);
        Toast.success("User access role updated successfully");
        queryClient.invalidateQueries({
          queryKey: platformUsersKeys.all,
        });
      })
      .catch((error) => {
        trackEvent(
          `User failed to change access role for user ${email} to ${role}`
        );
        if (error.response && error.response.status === 403) {
          Toast.error(error.response.data);
        } else {
          Toast.error(
            "An error occurred while trying to update the access role"
          );
        }
      })
      .finally(() => {
        setSelectedRoleChange(null);
      });
  };

  const UserPlatformTitleChange = async (email, value) => {
    await updatePlatformUserTitle(email, value)
      .then((response) => {
        trackEvent(`User changed title for user ${email} to ${value}`);
        Toast.success("User title updated successfully");
        queryClient.invalidateQueries({
          queryKey: platformUsersKeys.all,
        });
      })
      .catch((error) => {
        trackEvent(`User failed to change title for user ${email} to ${value}`);
        if (error.response && error.response.status === 403) {
          Toast.error(error.response.data);
        } else {
          Toast.error("An error occurred while trying to update the title");
        }
      });
  };

  const columns = [
    {
      field: "display_name",
      headerName: "User",
      headerAlign: "left",
      align: "left",
      flex: 0.12,
      renderHeader: (params) => {
        return (
          <Typography variant="subtitle2" sx={{ marginLeft: "16px" }}>
            {params.colDef.headerName}
          </Typography>
        );
      },
      renderCell: (params) => {
        return (
          <Typography variant="caption" sx={{ marginLeft: "16px" }}>
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      headerAlign: "left",
      align: "left",
      flex: 0.15,
      valueGetter: (params) => getDisplayEmail(params.row),
      renderCell: (params) => {
        const email = getDisplayEmail(params.row);
        return <Typography variant="caption">{email}</Typography>;
      },
    },
    {
      field: "role",
      headerName: "Title",
      headerAlign: "left",
      align: "left",
      flex: 0.12,
      renderCell: (params) => {
        return currentPlatformUser.data?.is_admin ? (
          <FieldMenu
            value={params.value}
            valuesList={titleOptions}
            onClick={(value) =>
              UserPlatformTitleChange(params.row.email, value)
            }
          />
        ) : (
          <Typography variant="caption">{params.value}</Typography>
        );
      },
    },
    {
      field: "access_role",
      headerName: "Role",
      headerAlign: "left",
      align: "left",
      flex: 0.1,
      valueFormatter: (params) => {
        return params.value;
      },
      renderCell: (params) => {
        let value = params.value;
        if (value === UserPlatformAccessRole.MSSP)
          value = UserPlatformAccessRole.ADMIN;
        else if (value === UserPlatformAccessRole.MSSP_VIEWER)
          value = UserPlatformAccessRole.VIEWER;
        return currentPlatformUser.data?.is_admin ? (
          <FieldMenu
            value={value}
            valuesList={Object.values(UserPlatformAccessRole).filter(
              (role) => !MSSP_PLATFORM_ACCESS_ROLES.includes(role)
            )}
            onClick={(value) => handleAccessRoleChange(params.row.email, value)}
          />
        ) : (
          <Typography variant="caption">{value}</Typography>
        );
      },
    },
    {
      field: "ownerships",
      headerName: "Ownerships",
      align: "left",
      headerAlign: "left",
      flex: 0.1,
      valueFormatter: (value) => {
        if (Array.isArray(value.value)) {
          return value.value
            .map((val) => IntegrationsMapping[val]?.displayName || val)
            .join(", ");
        }
      },
      renderCell: (params) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {params.value.map((ownership, index) => (
              <CustomToolTip
                key={index}
                title={IntegrationsMapping[ownership]?.displayName}
                placement="top"
              >
                {IntegrationsMapping[ownership]?.logo && (
                  <div>
                    <SvgToIcon
                      IconLogo={IntegrationsMapping[ownership]?.logo}
                      width="32px"
                    />
                  </div>
                )}
              </CustomToolTip>
            ))}
          </div>
        );
      },
    },
    {
      field: "last_seen",
      headerName: "Last Seen",
      align: "left",
      headerAlign: "left",
      flex: 0.1,
      valueFormatter: (params) =>
        getPrettyDateTime(params?.value?.timestamp_utc, showLocalTime),
      renderCell: (params) => {
        const last_seen_display = getPrettyDateTime(
          params?.value?.timestamp_utc,
          showLocalTime
        );
        return (
          <Box style={{ whiteSpace: "nowrap" }}>
            <Typography variant="caption">{last_seen_display}</Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      headerAlign: "center",
      align: "right",
      disableExport: true,
      flex: 0.01,
      renderCell: (params) => {
        return currentPlatformUser.data?.is_admin ? (
          <Box sx={{ marginRight: "16px" }}>
            <DeleteOrEditMember
              platformUser={params.row}
              titleOptions={titleOptions}
            />
          </Box>
        ) : null;
      },
    },
  ];
  return (
    <>
      {platformUsersList ? (
        <StyledDataGrid
          columns={columns}
          rows={platformUsersList}
          pageSizeOptionsOverride={[8, 16, 20]}
          getRowId={(row) => row.email}
          apiRef={refToPass}
        />
      ) : (
        <LoadingIcon />
      )}
      <ChangeAccessRoleDialog
        isDialogOpen={isDialogOpen}
        handleDialogClose={handleDialogClose}
        handleConfirmRoleChange={handleConfirmRoleChange}
        role={selectedRoleChange?.role}
      />
    </>
  );
}
